import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const portfolioItems = [
  {
    title: 'CatchingOn',
    type: 'Marketing Agency',
    tags: ['Brand Identity', 'Contact Forms'],
    desc: 'A bold, modern website for a freelance marketing agency. Features arch-shaped image frames, lime yellow accents, and a clean layout that lets the work speak for itself — energetic and professional throughout.',
    improvements: 'A fresh online presence that reflects CatchingOn\'s brand and drives new client enquiries.',
    url: 'https://www.catchingon.co.uk',
    gradient: 'from-[#C5C3E8] via-[#9d9bc4] to-[#E8FF59]',
    label: 'CatchingOn',
   },
  // {
  //   title: 'Velvet & Co',
  //   type: 'Hair Salon & Spa',
  //   tags: ['Booking System', 'Gallery'],
  //   desc: 'A soft, magazine-inspired website for a luxury salon. Built with asymmetric layouts, a bento-grid gallery, multi-step booking wizard, and a frosted-glass navigation with slide-out drawer — warm and editorial throughout.',
  //   improvements: 'Booking enquiries doubled, with 70% now coming through the online wizard.',
  //   url: 'https://dc50o8m7wo3bb.cloudfront.net/',
  //   gradient: 'from-[#8B3A5C] via-[#D4738C] to-[#f3e8ec]',
  //   label: 'Velvet & Co',
  // },
  // {
  //   title: 'Iron & Oak Fitness',
  //   type: 'Fitness Studio',
  //   tags: ['Dashboard UI', 'Membership'],
  //   desc: 'A bold, data-dense dashboard-style site for a gym. Features a sidebar navigation, filterable class grid, trainer profiles in alternating full-width rows, and a membership comparison table — aggressive and athletic in every detail.',
  //   improvements: 'New membership sign-ups grew by 35% with the streamlined comparison and class filtering.',
  //   url: 'https://d22mipb0zpxbbw.cloudfront.net/',
  //   gradient: 'from-[#0a0a0a] via-[#1a1a1a] to-[#e63946]',
  //   label: 'Iron & Oak',
  // },
  // {
  //   title: 'Flour & Fold',
  //   type: 'Bakery & Café',
  //   tags: ['Storytelling', 'Illustrated'],
  //   desc: 'A gentle, scroll-driven narrative site for a neighbourhood bakery. Hand-drawn SVG illustrations, wave-shaped section dividers, a Polaroid-style gallery, and an auto-hiding navbar create a warm, unhurried feel that matches the brand perfectly.',
  //   improvements: 'Footfall increased noticeably after launch, with customers citing the website as their first impression.',
  //   url: 'https://d3mutnyqrnbcgv.cloudfront.net',
  //   gradient: 'from-[#7C4A1E] via-[#B45309] to-[#f0e6d6]',
  //   label: 'Flour & Fold',
  // },
];

const Portfolio = () => {
  return (
    <div data-testid="portfolio-page">
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-6">
              Our Work
            </h1>
            <p className="text-lg text-muted-foreground">
              Take a look at some of the websites we've built for local businesses across the UK. 
              Each project is tailored to the client's needs and goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {portfolioItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="overflow-hidden border-border/50 hover:shadow-xl transition-all duration-300 group">
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <div className={`w-full h-full bg-gradient-to-br ${item.gradient} flex items-center justify-center transition-transform duration-500 group-hover:scale-105`}>
                      <span className="font-heading text-2xl md:text-3xl font-bold text-white/90 drop-shadow-lg">{item.label}</span>
                    </div>
                    <div className="absolute inset-0 bg-primary/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <Button
                        asChild
                        variant="secondary"
                        className="rounded-full"
                        data-testid={`portfolio-view-${index}`}
                      >
                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                          View Live Site <ExternalLink className="ml-2 w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="secondary" className="bg-accent/10 text-accent border-0">
                        {item.type}
                      </Badge>
                      {item.tags.map((tag, i) => (
                        <Badge key={i} variant="outline" className="text-muted-foreground">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                      {item.title}
                    </h2>
                    <p className="text-muted-foreground mb-4">{item.desc}</p>
                    <div className="pt-4 border-t border-border">
                      <p className="text-sm">
                        <span className="font-semibold text-accent">Result: </span>
                        <span className="text-muted-foreground">{item.improvements}</span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
              Want Similar Results?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Every business is different. Let's chat about what you need and how we can help.
            </p>
            <Button
              asChild
              data-testid="portfolio-cta-btn"
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg"
            >
              <Link to="/contact">Start Your Project</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
